import type { CancelToken } from 'axios';
import noop from 'lodash/noop';
import Heap from 'heap';
import { sleep } from './timing';
import { neverCancel } from './async';
import { TaskUrgency, type TaskPriority } from './taskPriority';

export function makePriority(options?: Partial<TaskPriority>): TaskPriority {
  return {
    urgency: options?.urgency ?? TaskUrgency.Default,
    serial: options?.serial ?? nextSerial()
  };
}

class WaitSlot {
  cancelled = false;
  constructor(public priority: TaskPriority, public readonly resolve: () => void) {}
}

function orderWaitSlots(a: WaitSlot, b: WaitSlot) {
  const ap = a.priority;
  const bp = b.priority;
  return (ap.urgency - bp.urgency) || (ap.serial - bp.serial);
}

let serial = 0;

export function nextSerial() {
  return ++serial;
}

/**
 * Semaphore to asynchronously limit execution to use a limited number of resources.
 */
export class PrioritySemaphore {
  max: number;

  pending: Heap<WaitSlot>;

  constructor(private resourceCount: number) {
    this.pending = new Heap(orderWaitSlots);
    if (resourceCount < 1) {
      throw new Error('At least one resource needed');
    }
    this.max = resourceCount;
  }

  async limit<T>(task: () => Promise<T>, priority: TaskPriority, cancelToken: CancelToken | null, interval?: number): Promise<T> {
    cancelToken?.throwIfRequested();
    if (this.resourceCount < 1) {
      await this.consumeWhenAvailable(priority, cancelToken);
      cancelToken?.throwIfRequested();
    } else {
      --this.resourceCount;
    }

    try {
      const taskResult = await task();
      if (interval) {
        await sleep(interval, cancelToken ?? neverCancel);
      }
      return taskResult;
    } finally {
      ++this.resourceCount;
      this.wake();
    }
  }

  private consumeWhenAvailable(priority: TaskPriority, cancelToken: CancelToken | null): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const slot = new WaitSlot(priority, resolve);
      cancelToken?.promise.then(cancel => {
        slot.cancelled = true;
        reject(cancel);
      }).catch(noop);
      this.pending.push(slot);
    });
  }

  private wake() {
    while (true) {
      const next = this.pending.peek();
      if (!next) {
        return;
      }

      if (next.cancelled) {
        this.pending.pop();
        continue;
      }

      if (this.resourceCount < 1) {
        return;
      }

      this.pending.pop();
      --this.resourceCount;
      next.resolve();
    }
  }
}
